<template>
    <div
        v-if="isStatic"
        :id="id"
        class="container-block"
        :data-direction="direction"
        :data-index-path="indexPath"
        :style="containerStyle"
        :class="containerClass"
    >
        <template
            v-for="(child) in children"
            :key="child.id"
        >
            <CustomPageBlock
                :block="child"
                :model-value="child.currentValue"
                :data-testid="`custom-page-block-${kebabCase((child as ExtendedBlock<CPInputConfiguration>).fieldName || child.type)}`"
            />
        </template>
    </div>
    <RevealBehaviorTile
        v-else
        :reveal-behavior="effectiveRevealBehavior"
        :reveal-icon="revealIcon"
        :children="children"
        class="container-tile"
        :style="containerStyle"
        :class="containerClass"
    />
</template>

<script setup lang="ts">
import { computed, type ComputedRef, inject, provide } from 'vue'
import { kebabCase } from 'change-case'
import type {
    ContainerProps,
    CPInputConfiguration,
    ExtendedBlock,
} from 'widget-components/integrations/CustomPage/types'
import type { StickyPosition } from 'widget-components/components/Container/container-sticky-position'
import { CustomPageBlock } from 'widget-components/integrations/CustomPage'
import type { Breakpoint } from 'widget-components/composables/breakpoints'
import { useBreakpoints } from 'widget-components/composables/breakpoints'
import RevealBehaviorTile from 'widget-components/components/RevealBehaviorTile/RevealBehaviorTile.vue'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
    id?: string
    children: ExtendedBlock[]
    direction?: ContainerProps['direction']
    gap?: string
    alignItems?: string
    justifyContent?: string
    flexMultiplier?: number
    stickyPosition?: StickyPosition
    responsiveVisibility?: 'always' | Breakpoint
    revealBehavior?: ContainerProps['revealBehavior']
    revealIcon?: string
    indexPath?: number[]
}>(), {
    direction: 'row',
    gap: '10px',
    alignItems: 'start',
    justifyContent: 'start',
    flexMultiplier: 1,
    responsiveVisibility: 'always',
    revealBehavior: 'STATIC',
})

// disable loading states inside inline-text containers
const isLoading = inject<ComputedRef<boolean>>('isLoading')
const effectiveIsLoading = computed(() => isLoading?.value && props.direction !== 'inline-text')
provide('isLoading', effectiveIsLoading)

const { sm } = useBreakpoints()

const isStatic = computed(() => {
    if (props.revealBehavior === 'STATIC') {
        return true
    }
    if (props.revealBehavior === 'INLINE_COLLAPSED_SM') {
        return !sm.value
    }
    return false
})

const effectiveRevealBehavior = computed(() => {
    if (props.direction === 'column' || (props.direction === 'responsive' && sm.value)) {
        return props.revealBehavior
    }
    return 'STATIC'
})

const containerStyle = computed(() => ({
    '--direction': props.direction !== 'responsive' ? props.direction : undefined,
    '--gap': props.gap,
    '--align-items': props.alignItems,
    '--justify-content': props.justifyContent,
}))

const containerClass = computed(() => ({
    [`${props.responsiveVisibility}-visible`]: props.responsiveVisibility !== 'always',
    'direction-responsive': props.direction === 'responsive',
    'direction-inline-text': props.direction === 'inline-text',
}))

</script>

<style lang="sass" scoped>
@import "./Container.sass"
</style>
